import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { PaymentInstrumentAuthorize, PaymentInstrumentPaypal, UserAddress } from '@bpm-web-app/supreme-api-sdk';
import { SubmitHandler, useForm } from 'react-hook-form';
import Joi from 'joi';
import { toast } from 'react-toastify';
import { joiResolver } from '@hookform/resolvers/joi';
import { showToast } from '@bpm-web-app/utils';
import styles from './account-add-payment.module.css';
import CloseIcon from '../../../assets/icons/close.svg';
import PencilIcon from '../../../assets/icons/pencil.svg';
import CardIcon from '../../../assets/icons/card.svg';

export interface PaymentData {
    billing?: {
        streetAddress: string;
        number: string;
        city: string;
        state: string;
        country: string;
        zipCode: string;
    };
    payment: {
        card?: {
            firstName: string;
            lastName: string;
            cardNumber: string;
            securityCode: string;
            expMonth: string;
            expYear: string;
        };
        stripe?: {
            payment_method_id: string;
        };
    };
}

export interface PaymentProps {
    billingAddress: UserAddress;
    paymentData: PaymentInstrumentAuthorize | PaymentInstrumentPaypal;
    billingText: string;
    isOpen: boolean;
    setPaymentData: (item: PaymentData) => void;
    isToSubmit?: boolean;
    onSubmit?: () => Promise<void>;
}
interface Inputs {
    firstName: string;
    lastName: string;
    cardNumber: string;
    securityCode: string;
    expMonth: string;
    expYear: string;
    streetAddress: string;
    number: string;
    city: string;
    state: string;
    country: string;
    zipCodeBilling: string;
}

const months = Array.from({ length: 12 }, (_, i) => i + 1);
const years = Array.from(new Array(20), (val, index) => index + new Date().getFullYear());

function isPaymentInstrumentCard(paymentData?: PaymentInstrumentAuthorize | PaymentInstrumentPaypal): paymentData is PaymentInstrumentAuthorize {
    return paymentData !== undefined && 'card' in paymentData;
}

export function AccountAddPayment({ billingText, setPaymentData, isOpen, paymentData, billingAddress, isToSubmit = false, onSubmit }: PaymentProps) {
    const [showForm, setShowForm] = useState(isOpen);
    const [formType, setFormType] = useState<'CC'>('CC');
    const [paymentMethodText, setPaymentMethodText] = useState<string | null>(null);
    const [isSubmit, setIsSubmit] = useState(false);

    const schema = Joi.object({
        firstName: Joi.string().required().messages({ 'string.empty': 'First name is required. Please try again.' }),
        lastName: Joi.string().required().messages({ 'string.empty': 'Last name is required. Please try again.' }),
        cardNumber: Joi.string().creditCard().required().messages({
            'string.empty': 'Card number is required. Please try again.',
            'string.creditCard': 'Card Number invalid. Please try again.',
        }),
        securityCode: Joi.string().min(3).max(4).required().messages({
            'string.empty': 'Security code is required. Please try again.',
            'string.min': 'Security code invalid. Please try again.',
            'string.max': 'Security code invalid. Please try again.',
        }),
        expMonth: Joi.string().required().messages({ 'string.empty': 'Exp month is required. Please try again.' }),
        expYear: Joi.string().required().messages({ 'string.empty': 'Exp year is required. Please try again.' }),
        streetAddress: Joi.string().required().messages({ 'string.empty': 'Street address is required. Please try again.' }),
        number: Joi.string().optional().allow(null, ''),
        city: Joi.string().required().messages({ 'string.empty': 'City is required. Please try again.' }),
        state: Joi.string().required().messages({ 'string.empty': 'State is required. Please try again.' }),
        country: Joi.string().required().messages({ 'string.empty': 'Country is required. Please try again.' }),
        zipCodeBilling: Joi.string().required().messages({ 'string.empty': 'Zip code is required. Please try again.' }),
    });
    const {
        register,
        handleSubmit,
        formState: { errors, submitCount },
    } = useForm<Inputs>({
        defaultValues: useMemo(
            () => ({
                // Billing
                firstName: billingAddress?.first_name,
                lastName: billingAddress?.last_name,
                streetAddress: billingAddress?.line1,
                number: billingAddress?.line2,
                city: billingAddress?.city,
                state: billingAddress?.state,
                country: billingAddress?.country,
                zipCodeBilling: billingAddress?.zip,
            }),
            []
        ),
        resolver: joiResolver(schema),
    });

    useEffect(() => {
        const handleFormSubmit = async () => {
            if (isSubmit) {
                // submit flow is handled in the parent component
                await onSubmit?.();
                setIsSubmit(false);
            }
        };
        handleFormSubmit();
    }, [isSubmit, onSubmit]);

    useEffect(() => {
        if (isPaymentInstrumentCard(paymentData) && paymentData?.card?.cardNumber) {
            setPaymentMethodText(`Credit card ending ${paymentData?.card?.cardNumber}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData]);

    useEffect(() => {
        if (Object.keys(errors).length !== 0) {
            toast.dismiss();
            Object.keys(errors).forEach((key) => {
                if (errors?.[key as keyof Inputs]?.message) {
                    showToast({ type: 'error', title: errors?.[key as keyof Inputs]?.message });
                }
            });
        }
    }, [errors, submitCount]);

    const handlerCC: SubmitHandler<Inputs> = (data) => {
        setPaymentData({
            billing: {
                streetAddress: data.streetAddress,
                number: data.number,
                city: data.city,
                state: data.state,
                country: data.country,
                zipCode: data.zipCodeBilling,
            },
            payment: {
                card: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    cardNumber: data.cardNumber,
                    securityCode: data.securityCode,
                    expMonth: data.expMonth.padStart(2, '0'),
                    expYear: data.expYear,
                }
            },
        });
        if (isToSubmit) {
            setIsSubmit(true);
        }
        setShowForm(false);
        setPaymentMethodText(`Credit card ending ${data.cardNumber.slice(data.cardNumber.length - 4)}`);
    };

    const getForm = () => {
        if (formType === 'CC') {
            return (
                <form onSubmit={handleSubmit(handlerCC)} className={styles['account-add-payment__form']}>
                    <h5>Card Information</h5>
                    <div className={styles['account-add-payment__form-content']}>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2'])}>
                            <input className="generic__input" placeholder="First name" {...register('firstName', { required: true })} />
                            <input className="generic__input" placeholder="Last name" {...register('lastName', { required: true })} />
                        </div>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2-bigger'])}>
                            <input className="generic__input" placeholder="Card number" {...register('cardNumber', { required: true })} />
                            <input className="generic__input" placeholder="Security code" {...register('securityCode', { required: true })} />
                        </div>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2'])}>
                            <select {...register('expMonth')} className="generic__input account__select">
                                {months.map((month) => (
                                    <option key={month} value={month}>
                                        {month.toLocaleString('en-US', {
                                            minimumIntegerDigits: 2,
                                        })}
                                    </option>
                                ))}
                            </select>
                            <select {...register('expYear')} className="generic__input account__select">
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <h5>Billing Address</h5>
                    <div className={styles['account-add-payment__form-content']}>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2'])}>
                            <input className="generic__input" placeholder="Street Address" {...register('streetAddress', { required: true })} />
                            <input className="generic__input" placeholder="Suite, Apt, etc." {...register('number', { required: true })} />
                        </div>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2'])}>
                            <input className="generic__input" placeholder="City" {...register('city', { required: true })} />
                            <input className="generic__input" placeholder="State" {...register('state', { required: true })} />
                        </div>
                        <div className={classNames(styles['account-add-payment__form-grid'], styles['account-add-payment__form-grid--2'])}>
                            <input className="generic__input" placeholder="Country" {...register('country', { required: true })} />
                            <input className="generic__input" placeholder="Zip Code" {...register('zipCodeBilling', { required: true })} />
                        </div>
                    </div>
                    <button className={classNames('account__button', styles['account-add-payment__form-button'])} type="submit">
                        {' '}
                        Save{' '}
                    </button>
                </form>
            );
        }
        return null;
    };

    return (
        <>
            <div className={styles['account-add-payment__information']}>
                <h3 className={styles['account-add-payment__information-title']}>Payment Details</h3>
                <div className={styles['account-add-payment__information-container']}>
                    <span className={styles['account-add-payment__information-text']}>{billingText}&nbsp;</span>
                    <span className={styles['account-add-payment__information-card']}>
                        {paymentMethodText}
                        <button aria-label="open payment details" className={styles['account-add-payment__information-icon']} onClick={() => setShowForm(!showForm)} type="button">
                            {showForm ? <CloseIcon /> : <PencilIcon />}
                        </button>
                    </span>
                </div>
            </div>
            {showForm ? (
                <>
                    <div className={styles['account-add-payment__payment-methods']}>
                        <button onClick={() => setFormType('CC')} className={classNames('account__button', formType !== 'CC' && 'account__button--inactive')} type="button">
                            <CardIcon />
                            Credit Card
                        </button>
                    </div>
                    {getForm()}
                </>
            ) : null}
        </>
    );
}

export default AccountAddPayment;
